import React from 'react';
import { useSelector } from 'react-redux';
import { isFinite, isNil } from 'lodash';
import { useLocale } from 'locale';
import { State } from 'store/state';
import { isInLastViewedSelector } from 'store/state/selectors/poi';
import { BulletinUniversalCardProps, CardType } from 'components/cards/types';
import { UniversalCardExtraProps } from 'components/cards/new-layout/types';
import CardImageWithTags from 'components/cards/new-layout/list-view-card/CardImageWithTags';
import FavouritesCardButton from 'components/cards/new-layout/components/FavouritesCardButton';
import CompareCheckbox from 'components/cards/new-layout/components/CompareCheckbox';
import Tag, { TagType } from 'components/cards/new-layout/components/Tag';
import { InvestorsCardPrice } from 'components/cards/investors-card/Price';
import { InvestorsCardTagsRow } from 'components/cards/investors-card/TagsRow';
import { InvestorsCardDetailsLine } from 'components/cards/investors-card/DetailsLine';
import { RenewalProgramIndicator, RenewalProgramStatus } from 'components/real-estate-listing-card/RenewalProgramIndicator';
import { getInvestorMetrics } from 'components/real-estate-listing-card/utils';
import * as InvestorCardStyled from 'components/real-estate-listing-card/styled';
import * as Styled from './styled';


export type InvestorsMobileListingCardProps = BulletinUniversalCardProps & UniversalCardExtraProps & { isURPotential?: boolean; };

const InvestorsMobileListingCard: React.FC<InvestorsMobileListingCardProps> = (props) => {
  const {
    id,
    type,
    thumbnails,
    logoPath,
    officeId,
    price,
    eventsHistory,
    isAgent,
    isExclusive,
    madadSearchResult,
    date,
    lastUpdatedDate,
    dealType,
    bedsCount,
    floor,
    size,
    area,
    buildingClass,
    streetName,
    streetNumber,
    neighbourhood,
    unitNumber,
    city,
    resolutionPreferences,
    isSearchInWholeCountry,
    showSchoolInAreaLabel,
    investorsData,
    gainAfterRenewal,
    urbanRenewalStatus,
    isURPotential,
  } = props;

  const { t } = useLocale();
  const isInLastViewed = useSelector((state: State) => isInLastViewedSelector(state, id));

  // the search API returns `floor` as a numeric string; InvestorsCardDetailsLine only renders
  // it when it is a number, so coerce before handing it over
  const numericFloor = isNil(floor) ? null : Number(floor);
  const detailsFloor = isFinite(numericFloor) ? numericFloor : undefined;

  const noDataText = t('investorListingCard.renewalStatus.noData');
  const { yieldValue, gainPercentageAfterRenewal } = getInvestorMetrics(investorsData, gainAfterRenewal, noDataText);

  return (
    <Styled.CardWrapper data-auto={`investors_mobile_listing_card_${id}`}>
      <Styled.ImageArea>
        <Styled.ImageWrapper>
          <CardImageWithTags
            cardType={CardType.Bulletin}
            small
            hideTags
            logoPath={logoPath}
            thumbnails={thumbnails}
            logoSourceId={officeId || null}
            eventsHistory={eventsHistory || []}
            hasDiscount={false}
            isPromoted={false}
            id={id}
            date={date}
            lastUpdatedDate={lastUpdatedDate}
            dealType={dealType}
            showSchoolInAreaLabel={Boolean(showSchoolInAreaLabel)}
          />
        </Styled.ImageWrapper>
        <Styled.FavoriteOverlay>
          <FavouritesCardButton id={id} type={type} size="large" />
        </Styled.FavoriteOverlay>
        <Styled.CompareOverlay>
          <CompareCheckbox id={id} />
        </Styled.CompareOverlay>
      </Styled.ImageArea>

      <Styled.Body>
        <InvestorsCardPrice price={price} eventsHistory={eventsHistory} />
        <InvestorsCardTagsRow
          eventsHistory={eventsHistory}
          isAgent={isAgent}
          isExclusive={isExclusive}
          madadSearchResult={madadSearchResult}
          createdDate={date}
          lastUpdatedDate={lastUpdatedDate}
          dealType={dealType}
          isInLastViewed={isInLastViewed}
        >
          {isURPotential && !isNil(urbanRenewalStatus) ? (
            <Tag
              type={TagType.URPotential}
              label={urbanRenewalStatus === RenewalProgramStatus.PermitGranted ? t('card.tag.ur') : undefined}
            />
          ) : null}
        </InvestorsCardTagsRow>
        <InvestorsCardDetailsLine
          bedsCount={bedsCount}
          floor={detailsFloor}
          size={size || area}
          buildingClass={buildingClass}
          streetName={streetName}
          streetNumber={streetNumber}
          neighbourhood={neighbourhood}
          unitNumber={unitNumber}
          city={city}
          resolutionPreferences={resolutionPreferences}
          isSearchInWholeCountry={isSearchInWholeCountry}
        />

        {isURPotential && !isNil(urbanRenewalStatus) ? (
          <>
            <Styled.IndicatorRow>
              <RenewalProgramIndicator status={urbanRenewalStatus} small noMargin />
            </Styled.IndicatorRow>
            <InvestorCardStyled.MetricsGrid>
              <InvestorCardStyled.MetricBox>
                <InvestorCardStyled.MetricLabel>
                  {t('investorListingCard.metrics.estimatedRentalYield')}
                </InvestorCardStyled.MetricLabel>
                <InvestorCardStyled.MetricValue>{yieldValue}</InvestorCardStyled.MetricValue>
              </InvestorCardStyled.MetricBox>
              <InvestorCardStyled.MetricBox>
                <InvestorCardStyled.MetricLabel>
                  {t('investorListingCard.metrics.gainPercentageAfterRenewal')}
                </InvestorCardStyled.MetricLabel>
                <InvestorCardStyled.MetricValue direction="ltr">
                  {gainPercentageAfterRenewal}
                </InvestorCardStyled.MetricValue>
              </InvestorCardStyled.MetricBox>
            </InvestorCardStyled.MetricsGrid>
          </>
        ) : null}
      </Styled.Body>
    </Styled.CardWrapper>
  );
};

export default InvestorsMobileListingCard;
